import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { fetchCountyScores, type CountyScore } from '../lib/api';

const C = designTokens.colors;
const T = designTokens.typography;

const fmtNum = (n: number | null | undefined) =>
  n != null ? Math.round(Number(n)).toLocaleString() : '-';

const getCompositeColor = (score: number): string => {
  if (score >= 70) return C.dcPrime;
  if (score >= 50) return C.dcStrong;
  if (score >= 30) return C.dcViable;
  return C.dcWeak;
};

interface Weights {
  gas: number;
  land: number;
  acquisition: number;
  siteQuality: number;
}

interface Knockouts {
  minPipelines: number | null;
  minAcreage: number | null;
  minFiberPct: number | null;
  maxSubstationMiles: number | null;
}

const DEFAULT_WEIGHTS: Weights = { gas: 1, land: 1, acquisition: 1, siteQuality: 1 };
const DEFAULT_KNOCKOUTS: Knockouts = {
  minPipelines: null,
  minAcreage: null,
  minFiberPct: null,
  maxSubstationMiles: null,
};

const STORAGE_KEY = 'pp-county-settings';

function loadSettings(): { weights: Weights; knockouts: Knockouts } {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const { minPipelines, minAcreage, minFiberPct, maxSubstationMiles } = { ...DEFAULT_KNOCKOUTS, ...parsed.knockouts };
      return {
        weights: { ...DEFAULT_WEIGHTS, ...parsed.weights },
        knockouts: { minPipelines, minAcreage, minFiberPct, maxSubstationMiles },
      };
    }
  } catch { /* noop */ }
  return { weights: DEFAULT_WEIGHTS, knockouts: DEFAULT_KNOCKOUTS };
}

function saveSettings(weights: Weights, knockouts: Knockouts) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ weights, knockouts }));
  } catch { /* noop */ }
}

type SortField = 'composite' | 'gas' | 'land' | 'acquisition' | 'siteQuality' | 'pipelines' | 'prime' | 'acreage';

function computeComposite(c: CountyScore, w: Weights): number {
  const sum = w.gas + w.land + w.acquisition + w.siteQuality;
  if (sum === 0) return 0;
  return (
    c.gas_infrastructure_score * w.gas +
    c.land_availability_score * w.land +
    c.acquisition_signals_score * w.acquisition +
    c.site_quality_score * w.siteQuality
  ) / sum;
}

function passesKnockouts(c: CountyScore, k: Knockouts): boolean {
  if (k.minPipelines != null && c.total_pipeline_count < k.minPipelines) return false;
  // Parcel-derived knockouts — skip for counties without parcel data
  if (c.has_parcel_data) {
    if (k.minAcreage != null && Number(c.total_suitable_acreage) < k.minAcreage) return false;
    if (k.minFiberPct != null && Number(c.fiber_available_pct) < k.minFiberPct) return false;
    if (k.maxSubstationMiles != null && c.avg_substation_miles != null && Number(c.avg_substation_miles) > k.maxSubstationMiles) return false;
  }
  return true;
}

const printStyles = `
@media print {
  .county-toolbar { display: none !important; }
  @page { margin: 0.4in; size: letter landscape; }
}
`;

const anyKnockoutActive = (k: Knockouts) =>
  k.minPipelines != null || k.minAcreage != null || k.minFiberPct != null || k.maxSubstationMiles != null;

export const CountyReport: React.FC<{
  onSelectCounty?: (state: string, county: string, rank?: number, composite?: number) => void;
}> = ({ onSelectCounty }) => {
  const { theme } = useTheme();
  const [counties, setCounties] = useState<CountyScore[] | null>(null);
  const [computedAt, setComputedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [weights, setWeights] = useState<Weights>(() => loadSettings().weights);
  const [knockouts, setKnockouts] = useState<Knockouts>(() => loadSettings().knockouts);
  const [stateFilter, setStateFilter] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('composite');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');


  useEffect(() => { saveSettings(weights, knockouts); }, [weights, knockouts]);

  useEffect(() => {
    let cancelled = false;
    fetchCountyScores()
      .then(data => { if (!cancelled) { setCounties(data.counties); setComputedAt(data.computed_at); } })
      .catch(() => { if (!cancelled) setError('Failed to load county scores'); });
    return () => { cancelled = true; };
  }, []);

  const rankedCounties = useMemo(() => {
    if (!counties) return [];
    let filtered = counties;
    if (stateFilter) filtered = filtered.filter(c => c.state === stateFilter);

    const scored = filtered.map(c => ({
      ...c,
      composite: Math.round(computeComposite(c, weights)),
      knockedOut: !passesKnockouts(c, knockouts),
      rank: 0,
    }));

    const sortFn = (a: typeof scored[0], b: typeof scored[0]) => {
      let va: number, vb: number;
      switch (sortField) {
        case 'composite': va = a.composite; vb = b.composite; break;
        case 'gas': va = a.gas_infrastructure_score; vb = b.gas_infrastructure_score; break;
        case 'land': va = a.land_availability_score; vb = b.land_availability_score; break;
        case 'acquisition': va = a.acquisition_signals_score; vb = b.acquisition_signals_score; break;
        case 'siteQuality': va = a.site_quality_score; vb = b.site_quality_score; break;
        case 'pipelines': va = a.total_pipeline_count; vb = b.total_pipeline_count; break;
        case 'prime': va = a.prime_site_count; vb = b.prime_site_count; break;
        case 'acreage': va = Number(a.total_suitable_acreage); vb = Number(b.total_suitable_acreage); break;
        default: va = a.composite; vb = b.composite;
      }
      return sortDir === 'desc' ? vb - va : va - vb;
    };

    const active = scored.filter(c => !c.knockedOut).sort(sortFn);
    const knockedOut = scored.filter(c => c.knockedOut).sort(sortFn);
    active.forEach((c, i) => { c.rank = i + 1; });
    return [...active, ...knockedOut];
  }, [counties, weights, knockouts, stateFilter, sortField, sortDir]);

  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(field); setSortDir('desc'); }
  }, [sortField]);

  const updateWeight = useCallback((key: keyof Weights, value: number) => {
    setWeights(w => ({ ...w, [key]: value }));
  }, []);

  const updateKnockout = useCallback(<K extends keyof Knockouts>(key: K, value: Knockouts[K]) => {
    setKnockouts(k => ({ ...k, [key]: value }));
  }, []);

  const activeCount = rankedCounties.filter(c => !c.knockedOut).length;
  const totalCount = rankedCounties.length;

  const tc = useMemo(() => ({
    background: theme.background,
    backgroundSecondary: theme.backgroundSecondary,
    surface: theme.surface,
    text: theme.text,
    textSecondary: theme.textSecondary,
    textTertiary: theme.textTertiary,
    border: theme.border,
    borderLight: theme.borderLight,
  }), [theme]);

  const thStyle: React.CSSProperties = {
    padding: '6px 8px', textAlign: 'left', fontWeight: 600,
    fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.06em',
    color: tc.textSecondary, background: tc.surface,
    fontFamily: '"JetBrains Mono", monospace',
    cursor: 'pointer', userSelect: 'none',
  };

  const sortArrow = (field: SortField) =>
    field !== sortField ? '' : sortDir === 'desc' ? ' \u25BE' : ' \u25B4';

  const weightsChanged = weights.gas !== 1 || weights.land !== 1 || weights.acquisition !== 1 || weights.siteQuality !== 1;
  const allWeightsZero = weights.gas === 0 && weights.land === 0 && weights.acquisition === 0 && weights.siteQuality === 0;

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', flex: 1, width: '100%', overflow: 'hidden',
      fontFamily: T.fontFamily.display, background: tc.background, color: tc.text,
    }}>
      <style>{printStyles}</style>

      {/* Sub-toolbar */}
      <div className="county-toolbar" style={{
        height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 20px', background: '#2E3840', borderBottom: '1px solid rgba(80,98,114,0.10)', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            style={{
              padding: '3px 8px', fontSize: '11px', fontFamily: T.fontFamily.display,
              background: '#3A4650', color: '#E4E6E8', border: '1px solid rgba(80,98,114,0.3)',
              borderRadius: '3px', cursor: 'pointer',
            }}
          >
            <option value="">All States</option>
            <option value="TX">Texas</option>
            <option value="OK">Oklahoma</option>
          </select>
          {counties && (
            <span style={{ fontSize: '11px', color: '#8A9CAA' }}>
              {activeCount} of {totalCount} counties{anyKnockoutActive(knockouts) ? ' (filtered)' : ''}
            </span>
          )}
        </div>
        <button onClick={() => window.print()} style={{
          padding: '4px 14px', background: C.chartPrimary, color: '#E4E6E8',
          border: 'none', borderRadius: '3px', fontSize: '11px', fontWeight: 500,
          cursor: 'pointer', fontFamily: T.fontFamily.display,
        }}>
          Export Brief
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <div className="county-document" style={{ maxWidth: '1100px', margin: '0 auto', padding: '32px 24px 64px' }}>

          {!counties && !error && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: tc.textTertiary }}>
              Loading county scores...
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '80px 0', color: C.error }}>{error}</div>
          )}

          {counties && (
            <>
              {/* Header */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  fontSize: '9px', fontWeight: 500, color: C.chartPrimary,
                  textTransform: 'uppercase', letterSpacing: '0.12em',
                  fontFamily: 'monospace',
                }}>County Intelligence</div>
                <div style={{ fontSize: '22px', fontWeight: 400, color: tc.text, fontFamily: 'Georgia, serif', lineHeight: 1.2 }}>County Scoring &amp; Rankings</div>
                <div style={{ fontSize: '11px', color: tc.textSecondary, marginTop: '4px' }}>
                  TX &middot; OK &nbsp;|&nbsp; {computedAt
                    ? new Date(computedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : 'Not computed'}
                </div>
              </div>

              {/* KPI cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '24px' }}>
                <KPICard label="Counties" value={fmtNum(activeCount)} color={C.chartPrimary} />
                <KPICard label="PRIME Sites" value={fmtNum(rankedCounties.filter(c => !c.knockedOut).reduce((s, c) => s + c.prime_site_count, 0))} color={C.dcPrime} />
                <KPICard label="Suitable Acres" value={fmtNum(rankedCounties.filter(c => !c.knockedOut).reduce((s, c) => s + Number(c.total_suitable_acreage), 0))} color={C.chartSecondary} />
              </div>

              <p style={{ fontSize: '10px', color: tc.textSecondary, marginTop: '-14px', marginBottom: '24px' }}>Pipeline geometry and count are shown as corridor context; local delivery and firm transportation remain unconfirmed.</p>

              {/* Controls: Weights + Knockouts */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                {/* Weight sliders */}
                <div style={{ background: tc.surface, borderRadius: '6px', padding: '12px 16px' }}>
                  <div style={{
                    fontSize: '9px', fontWeight: 600, color: C.chartPrimary,
                    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px',
                    fontFamily: 'monospace',
                  }}>
                    Category Weights
                  </div>
                  <WeightSlider label="Gas Infrastructure" value={weights.gas} onChange={v => updateWeight('gas', v)} />
                  <WeightSlider label="Land Availability" value={weights.land} onChange={v => updateWeight('land', v)} />
                  <WeightSlider label="Acquisition Signals" value={weights.acquisition} onChange={v => updateWeight('acquisition', v)} />
                  <WeightSlider label="Site Quality" value={weights.siteQuality} onChange={v => updateWeight('siteQuality', v)} />
                  {weightsChanged && (
                    <button
                      onClick={() => setWeights(DEFAULT_WEIGHTS)}
                      style={{
                        marginTop: '6px', padding: '3px 10px', fontSize: '9px',
                        background: 'none', border: `1px solid ${tc.border}`, borderRadius: '3px',
                        color: tc.textSecondary, cursor: 'pointer', fontFamily: 'monospace',
                      }}
                    >
                      Reset to 1.0x
                    </button>
                  )}
                </div>

                {/* Knockout filters */}
                <div style={{ background: tc.surface, borderRadius: '6px', padding: '12px 16px' }}>
                  <div style={{
                    fontSize: '9px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em',
                    color: C.chartPrimary, marginBottom: '10px',
                    fontFamily: 'monospace',
                  }}>
                    Knockout Filters
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <KnockoutRow label="Min Pipelines" chips={[1, 5, 10]} format={v => `${v}+`} active={knockouts.minPipelines} onSelect={v => updateKnockout('minPipelines', v)} />
                    <KnockoutRow label="Min Suitable Acreage" chips={[40, 100, 500]} format={v => `${v}`} active={knockouts.minAcreage} onSelect={v => updateKnockout('minAcreage', v)} />
                    <KnockoutRow label="Min Fiber Available" chips={[10, 30, 50]} format={v => `${v}%`} active={knockouts.minFiberPct} onSelect={v => updateKnockout('minFiberPct', v)} />
                    <KnockoutRow label="Max Substation Dist" chips={[5, 10, 25]} format={v => `${v} mi`} active={knockouts.maxSubstationMiles} onSelect={v => updateKnockout('maxSubstationMiles', v)} />
                    {anyKnockoutActive(knockouts) && (
                      <button
                        onClick={() => setKnockouts(DEFAULT_KNOCKOUTS)}
                        style={{
                          marginTop: '4px', padding: '3px 10px', fontSize: '9px',
                          background: 'none', border: `1px solid ${tc.border}`, borderRadius: '3px',
                          color: tc.textSecondary, cursor: 'pointer', fontFamily: 'monospace',
                        }}
                      >
                        Clear All Filters
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Ranked Table */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{
                  fontSize: '10px', fontWeight: 700, color: tc.text,
                  textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '6px',
                  borderBottom: `2px solid ${tc.textSecondary}`, paddingBottom: '4px',
                }}>
                  County Rankings
                </div>
                {allWeightsZero && (
                  <div style={{ fontSize: '11px', color: C.warning, marginBottom: '8px' }}>
                    Set at least one category weight above 0 to rank counties.
                  </div>
                )}
                <div style={{ background: tc.surface, borderRadius: '6px', overflow: 'hidden', border: `1px solid ${tc.borderLight}` }}>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', fontFamily: T.fontFamily.display, minWidth: '900px' }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${tc.border}` }}>
                          <th style={{ ...thStyle, textAlign: 'center', width: '30px', cursor: 'default' }}>#</th>
                          <th style={{ ...thStyle, cursor: 'default' }}>County, State</th>
                          <th onClick={() => handleSort('composite')} style={{ ...thStyle, textAlign: 'center' }}>Score{sortArrow('composite')}</th>
                          <th onClick={() => handleSort('gas')} style={{ ...thStyle, textAlign: 'center' }}>Gas{sortArrow('gas')}</th>
                          <th onClick={() => handleSort('land')} style={{ ...thStyle, textAlign: 'center' }}>Land{sortArrow('land')}</th>
                          <th onClick={() => handleSort('acquisition')} style={{ ...thStyle, textAlign: 'center' }}>Acq{sortArrow('acquisition')}</th>
                          <th onClick={() => handleSort('siteQuality')} style={{ ...thStyle, textAlign: 'center' }}>Quality{sortArrow('siteQuality')}</th>
                          <th onClick={() => handleSort('pipelines')} style={{ ...thStyle, textAlign: 'center' }}>Pipes{sortArrow('pipelines')}</th>
                          <th onClick={() => handleSort('prime')} style={{ ...thStyle, textAlign: 'center' }}>PRIME{sortArrow('prime')}</th>
                          <th onClick={() => handleSort('acreage')} style={{ ...thStyle, textAlign: 'right' }}>Acres{sortArrow('acreage')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rankedCounties.map((c, i) => (
                          <tr
                            key={`${c.state}-${c.county}`}
                            onClick={() => onSelectCounty?.(c.state, c.county, c.knockedOut ? undefined : c.rank, c.composite)}
                            style={{
                              borderBottom: i < rankedCounties.length - 1 ? `1px solid ${tc.borderLight}` : 'none',
                              background: i % 2 === 1 ? `${tc.surface}40` : 'transparent',
                              opacity: c.knockedOut ? 0.35 : 1,
                              cursor: onSelectCounty ? 'pointer' : 'default',
                            }}
                          >
                            <td style={{ padding: '5px 8px', textAlign: 'center', color: tc.textTertiary, fontFamily: T.fontFamily.mono, fontSize: '10px' }}>
                              {c.knockedOut ? '-' : c.rank}
                            </td>
                            <td style={{ padding: '5px 8px', fontWeight: 500 }}>
                              {c.county}, {c.state}
                              {!c.has_parcel_data && (
                                <span style={{
                                  marginLeft: '6px', padding: '1px 4px', fontSize: '8px', fontWeight: 600,
                                  background: C.warningLight, color: C.warning, borderRadius: '2px',
                                  letterSpacing: '0.03em',
                                }}>PARTIAL</span>
                              )}
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                              <span style={{
                                display: 'inline-block', padding: '1px 6px', borderRadius: '2px',
                                fontSize: '9px', fontWeight: 700, letterSpacing: '0.03em',
                                background: getCompositeColor(c.composite), color: '#E4E6E8', minWidth: '24px',
                              }}>
                                {c.composite}
                              </span>
                            </td>
                            <td style={{ padding: '5px 8px', textAlign: 'center', fontFamily: T.fontFamily.mono, fontSize: '10px' }}>{c.gas_infrastructure_score}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'center', fontFamily: T.fontFamily.mono, fontSize: '10px' }}>{c.land_availability_score}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'center', fontFamily: T.fontFamily.mono, fontSize: '10px' }}>{c.acquisition_signals_score}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'center', fontFamily: T.fontFamily.mono, fontSize: '10px' }}>{c.site_quality_score}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'center', fontFamily: T.fontFamily.mono }}>{c.total_pipeline_count}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'center', fontFamily: T.fontFamily.mono }}>{c.prime_site_count}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', fontFamily: T.fontFamily.mono }}>{fmtNum(c.total_suitable_acreage)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div style={{
                textAlign: 'center', color: tc.textTertiary, fontSize: '9px',
                borderTop: `1px solid ${tc.borderLight}`, paddingTop: '16px', marginTop: '32px',
                fontFamily: 'monospace',
              }}>
                County Intelligence Report &middot; {computedAt
                  ? new Date(computedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                  : 'Not computed'} &middot; Confidential
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* --- Sub-components --- */

const KPICard: React.FC<{
  label: string; value: string; color: string;
}> = ({ label, value, color }) => (
  <div style={{
    background: color + '18',
    borderRadius: '6px',
    padding: '12px 14px',
  }}>
    <div style={{ fontSize: '24px', fontWeight: 400, fontFamily: 'Georgia, serif', color, lineHeight: 1 }}>
      {value}
    </div>
    <div style={{
      fontSize: '9px', fontWeight: 500, color,
      textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: '4px',
      fontFamily: 'monospace',
    }}>
      {label}
    </div>
  </div>
);

const WeightSlider: React.FC<{
  label: string; value: number; onChange: (v: number) => void;
}> = ({ label, value, onChange }) => {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
      <div style={{ width: '130px', fontSize: '10px', color: theme.textSecondary, fontWeight: 500 }}>{label}</div>
      <input
        type="range" min={0} max={2} step={0.1}
        value={value}
        onChange={e => onChange(parseFloat(e.target.value))}
        style={{ flex: 1, height: '4px', accentColor: C.chartPrimary }}
      />
      <div style={{ width: '36px', fontSize: '10px', fontFamily: T.fontFamily.mono, color: theme.textSecondary, textAlign: 'right' }}>
        {value.toFixed(1)}x
      </div>
    </div>
  );
};

const KnockoutRow: React.FC<{
  label: string; chips: number[]; format: (v: number) => string; active: number | null; onSelect: (v: number | null) => void;
}> = ({ label, chips, format, active, onSelect }) => {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ fontSize: '10px', color: theme.textSecondary, fontWeight: 500 }}>{label}</div>
      <div style={{ display: 'flex', gap: '4px' }}>
        {chips.map(v => (
          <ChipButton key={v} label={format(v)} active={active === v} onClick={() => onSelect(active === v ? null : v)} />
        ))}
      </div>
    </div>
  );
};

const ChipButton: React.FC<{
  label: string; active: boolean; onClick: () => void;
}> = ({ label, active, onClick }) => {
  const { theme } = useTheme();
  return (
    <button
      onClick={onClick}
      style={{
        padding: '1px 6px', fontSize: '9px', fontFamily: T.fontFamily.mono,
        background: active ? C.chartPrimary : theme.surface,
        color: active ? '#E4E6E8' : theme.textSecondary,
        border: `1px solid ${active ? C.chartPrimary : theme.border}`, borderRadius: '2px',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
};
